import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { Menu, MenuButton, MenuItem } from "@szhsin/react-menu";
import { MoreVert as ThreeDotIcon } from "@mui/icons-material";

import { colors } from "../../utils";
import { hoverHighlight } from "../../theme/tokens";
import { HELP_FAB_Z_INDEX } from "../HelpAssistant/constants";
import { ensureMenuItemHoverStyleInjected } from "./menuItemHoverStyle";

// Same 32px hit target the wheel-card kebab uses (WheelCardThreeDotMenu,
// MenuPopup). Exported so circular collection badges size to contain it.
export const KEBAB_MENU_TOGGLE_SIZE = 32;

const DEFAULT_BOTTOM_LABELS = ["Delete"];

const menuItemStyle = (disabled) => ({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  padding: `${hoverHighlight.paddingY}px ${hoverHighlight.paddingX}px`,
  cursor: disabled ? "not-allowed" : "pointer",
  fontSize: "14px",
  fontFamily: "DM Sans, sans-serif",
  color: disabled ? "#9E9E9E" : colors.textBlue,
  borderRadius: hoverHighlight.radius,
  opacity: disabled ? 0.6 : 1,
});

const MENU_PANEL_STYLE = {
  border: "1px solid #ddd",
  borderRadius: "8px",
  padding: "8px",
  zIndex: HELP_FAB_Z_INDEX + 1,
  background: "white",
  boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
  width: "200px",
};

/**
 * Shared @szhsin/react-menu kebab used by wheel cards and collection tiles.
 * Accepts the same `{ label, icon, action(data), isDisabled? }` shape MenuPopup
 * used, so callers can swap without rewriting their menu builders.
 */
const KebabContextMenu = ({
  data,
  menuItems = [],
  ariaLabel = "More options",
  bottomLabels = DEFAULT_BOTTOM_LABELS,
  onMenuChange,
  onButtonClick,
}) => {
  // gordon-0922-P1-discover-trending-kebab-menu-clone-flash-share-delay-hover:
  // on-brand hover fill for the item rows below — see menuItemHoverStyle.js
  // for why this can't just rely on the library's own optional stylesheet.
  useEffect(() => {
    ensureMenuItemHoverStyleInjected();
  }, []);

  const topItems = menuItems.filter(
    (item) => !bottomLabels.includes(item.label)
  );
  const bottomItems = menuItems.filter((item) =>
    bottomLabels.includes(item.label)
  );

  const runItem = (item) => (event) => {
    // ISP-9999 (fix-stgfid0918b-collection-menu-click-opens-collection) —
    // @szhsin/react-menu's MenuItem onClick does NOT hand back the raw
    // DOM/React event: it wraps it as `{ value, syntheticEvent }` (see
    // MenuItem.cjs handleClick) and only the boolean `stopPropagation` flag
    // documented on that wrapper controls whether the ROOT MENU's internal
    // onItemClick fires — it is not a function. `event?.stopPropagation?.()`
    // therefore silently no-ops (optional chaining swallows the "not a
    // function" case) and the real click keeps bubbling through the REACT
    // tree (a `portal` menu bubbles by component tree, not DOM tree) up to
    // the collection card's onClick={open}, opening the collection behind
    // whatever dialog/toast the menu action shows. The real event to stop is
    // `event.syntheticEvent`.
    event?.syntheticEvent?.stopPropagation?.();
    if (item?.isDisabled) return;
    item?.action?.(data);
  };

  return (
    <Menu
      portal
      viewScroll="close"
      direction="bottom"
      onMenuChange={onMenuChange}
      menuButton={
        <MenuButton
          aria-label={ariaLabel}
          data-collection-menu-trigger="true"
          onClick={(event) => {
            event.stopPropagation();
            onButtonClick?.(event);
          }}
          style={{
            backgroundColor: "transparent",
            border: "none",
            cursor: "pointer",
            width: KEBAB_MENU_TOGGLE_SIZE,
            height: KEBAB_MENU_TOGGLE_SIZE,
            minWidth: KEBAB_MENU_TOGGLE_SIZE,
            minHeight: KEBAB_MENU_TOGGLE_SIZE,
            flexShrink: 0,
            padding: 0,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: 6,
          }}
        >
          <ThreeDotIcon fontSize="small" />
        </MenuButton>
      }
      menuStyle={MENU_PANEL_STYLE}
    >
      {topItems.map((item, index) => (
        <MenuItem
          key={`${item.label}-${index}`}
          disabled={!!item.isDisabled}
          onClick={runItem(item)}
          style={menuItemStyle(item.isDisabled)}
          aria-label={item.ariaLabel}
        >
          {item.icon} {item.label}
        </MenuItem>
      ))}

      {bottomItems.length > 0 && (
        <>
          <div style={{ height: 1, background: "#F7F2FA", margin: "6px 0" }} />
          {bottomItems.map((item, index) => (
            <MenuItem
              key={`${item.label}-bottom-${index}`}
              disabled={!!item.isDisabled}
              onClick={runItem(item)}
              style={menuItemStyle(item.isDisabled)}
              aria-label={item.ariaLabel}
            >
              {item.icon} {item.label}
            </MenuItem>
          ))}
        </>
      )}
    </Menu>
  );
};

KebabContextMenu.propTypes = {
  data: PropTypes.object,
  menuItems: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      icon: PropTypes.node,
      action: PropTypes.func,
      isDisabled: PropTypes.bool,
      ariaLabel: PropTypes.string,
    })
  ),
  ariaLabel: PropTypes.string,
  bottomLabels: PropTypes.arrayOf(PropTypes.string),
  onMenuChange: PropTypes.func,
  onButtonClick: PropTypes.func,
};

export default KebabContextMenu;
